// FRONT-638 / FRONT-773 / FRONT-2195: the re-attach machinery for ONE
// `useAgentChat` instance — everything that decides when to (re)connect to a
// chat turn still running on the server, and what the screen shows while it
// does. Lifted out of the hook so every step runs under a plain unit test
// with fake timers (the frontend suite has no DOM to mount the hook in); the
// hook only wires React state, the AI SDK chat and the query cache into
// `deps` and forwards its events here.
//
// Vocabulary:
//  - a PURSUIT is one effort to stay attached to one streamId. It starts by
//    stripping the trailing partial row off the screen and asking the AI SDK
//    to resume (the server replays the whole turn from its tape, then tails
//    it live), and it may take several ATTEMPTS (reconnect requests) when
//    one fails — between attempts the full snapshot is back on screen, so a
//    reconnect that keeps failing never hides the running answer.
//  - a pursuit ends one of four ways: a clean settle (the resume stream
//    closed without an error), finished (a refetched /history shows the turn
//    is no longer running), a give-up (budget or refusal — the partial is
//    restored and the bubble shows the reconnect caution), or a Stop.
//  - a RE-ARM is a new pursuit for the same streamId after a clean settle
//    while /history still reports the turn live (the resume endpoint closes
//    its tail at a ceiling; a proxy can drop an idle connection).
//
// Pure decisions stay in their own modules — the retry budget in
// resume-retry-policy.ts, re-arm eligibility and spacing in
// reattach-decision.ts, what /history says in resume-decision.ts. This file
// only sequences them.

import {
  resumeTargetFromHistory,
  streamIdsToSuppressOnStop,
  type ResumableMessage,
} from '~/hooks/resume-decision';
import {
  effectiveResumeStatus,
  hasResumeRecord,
  isTerminalResumeStatus,
  recordResumeAttempt,
  recordResumeFailure,
  recordResumeSuccess,
  releaseResumeRecordsForOwner,
} from '~/hooks/resume-retry-policy';
import {
  isEligibleForReattach,
  nextReattachCadence,
  type ReattachCadenceState,
} from '~/hooks/reattach-decision';

/** What the controller needs from the hook. Replaced on every render. */
export interface PursuitDeps<M extends ResumableMessage> {
  /** Replace the on-screen message list (`chat.setMessages`). */
  setMessages(messages: M[]): void;
  /**
   * Start one reconnect on the AI SDK chat (`chat.resumeStream`). Always
   * resolves; a failure is reported through `onStreamError` first.
   */
  resumeStream(): Promise<void>;
  /** GET /history now, write it into the query cache and return it. Rejects on failure. */
  refetchHistory(): Promise<M[]>;
  /** The freshest /history snapshot this instance holds. */
  latestHistory(): M[] | undefined;
  /** Whether the history query is mid-refetch right now. */
  isHistoryFetching(): boolean;
  /** The AI SDK chat status right now. */
  localStatus(): string;
  /**
   * A pursuit was armed for a turn the person has NOT stopped (a stopped
   * turn is suppressed and never armed) — the hook clears its composer-level
   * "stopped" flag so the composer shows Stop for this turn.
   */
  onPursuitArmed(streamId: string): void;
}

export interface ConsiderHistoryInput {
  /**
   * The hook's current session. A change resets everything first — the hook
   * instance can outlive a session (pwa-shell switches sessions on one
   * mounted instance), and nothing of the old session's pursuit may leak.
   */
  sessionId: string;
  /** The hook's `reattach` option — false for an instance nothing renders. */
  reattachEnabled: boolean;
  isFetching: boolean;
  localStatus: string;
}

/**
 * True for the AI SDK reconnect request this controller drives — the one URL
 * `prepareReconnectToStreamRequest` builds. The hook's transport fetch uses it
 * to attach the attempt's abort signal and record the response status.
 */
export function isResumeRequestUrl(url: string): boolean {
  return /\/api\/v2\/chat\/stream\/[^/?#]+\/resume(?:[?#]|$)/.test(url);
}

/** Delay before a re-arm when the settle recorded none (defensive default). */
const DEFAULT_REATTACH_DELAY_MS = 1_000;

let ownerSeq = 0;

export class ResumePursuitController<M extends ResumableMessage> {
  /** This instance's key in the resume-retry registry (FRONT-2195 review F6). */
  readonly owner = `pursuit-${++ownerSeq}`;

  constructor(public deps: PursuitDeps<M>) {}

  private mounted = false;
  private teardownTimer: ReturnType<typeof setTimeout> | null = null;
  /**
   * Bumped by every reset. An async continuation (a resolved stream, a
   * refetch, a timer) captured the epoch it started in and does nothing if
   * it changed — so no timer or pursuit is ever started for an instance that
   * unmounted or moved to another session (FRONT-2195 review F4).
   */
  private epoch = 0;
  /** Turns a deliberate Stop poisoned — never (re-)attached again (FRONT-765). */
  private suppressed = new Set<string>();
  /** Streams that have had at least one pursuit settle cleanly (re-arm vs first attach). */
  private settledOnce = new Set<string>();
  private resuming = false;
  /** The streamId of the current pursuit — attempt in flight OR backing off. */
  private activeStreamId: string | null = null;
  /** The streamId the transport's reconnect request is for, while one is being made. */
  private target: string | null = null;
  private attemptAbort: AbortController | null = null;
  /** The in-flight attempt's failure handler; null while backing off. */
  private attemptFailure: (() => void) | null = null;
  private lastResumeStatus: number | null = null;
  private retryTimer: ReturnType<typeof setTimeout> | null = null;
  private reattachTimer: ReturnType<typeof setTimeout> | null = null;
  /** Set from scheduling a re-arm until its timer has refetched and decided. */
  private reattachScheduledFor: string | null = null;
  private cadence = new Map<string, ReattachCadenceState>();
  private pursuitStartedAt = new Map<string, number>();
  private pendingReattachDelay = new Map<string, number>();
  /** The session this instance's state belongs to (null before the first look). */
  private sessionId: string | null = null;

  /**
   * True from stripping the partial until the pursuit ends — the hook's
   * reseed effect stands down meanwhile, or it would re-add the partial the
   * pursuit just stripped.
   */
  get isResuming(): boolean {
    return this.resuming;
  }

  /** The streamId the reconnect request must target, or null. */
  get resumeTargetStreamId(): string | null {
    return this.target;
  }

  /** The current attempt's abort signal — Stop cuts the resume connection with it. */
  resumeSignal(): AbortSignal | undefined {
    return this.attemptAbort?.signal;
  }

  /** The reconnect request came back with this HTTP status. */
  noteResumeResponse(status: number): void {
    this.lastResumeStatus = status;
  }

  // ── Lifecycle ────────────────────────────────────────────────────────────

  mount(): void {
    this.mounted = true;
    if (this.teardownTimer != null) {
      clearTimeout(this.teardownTimer);
      this.teardownTimer = null;
    }
  }

  /**
   * Deferred by one task: React StrictMode runs every effect's cleanup and
   * setup back to back on first mount, and a synchronous teardown there
   * would abort the first attempt and let the re-run arm a second one on the
   * same AI SDK chat while the first is still unwinding. A remount inside
   * that tick cancels the teardown; a real unmount runs it.
   */
  unmount(): void {
    this.mounted = false;
    if (this.teardownTimer != null) clearTimeout(this.teardownTimer);
    this.teardownTimer = setTimeout(() => {
      this.teardownTimer = null;
      if (!this.mounted) this.reset();
    }, 0);
  }


  /**
   * Forget everything this instance was doing — on unmount and on a session
   * change: timers cleared, the in-flight attempt aborted, its registry
   * records released so no record outlives the instance that could act on it.
   */
  private reset(): void {
    this.epoch += 1;
    this.clearRetryTimer();
    this.clearReattachTimer();
    this.attemptAbort?.abort();
    this.attemptAbort = null;
    this.attemptFailure = null;
    this.target = null;
    this.resuming = false;
    this.activeStreamId = null;
    this.suppressed = new Set();
    this.settledOnce = new Set();
    this.cadence = new Map();
    this.pursuitStartedAt = new Map();
    this.pendingReattachDelay = new Map();
    releaseResumeRecordsForOwner(this.owner);
  }

  // ── Events from the hook ─────────────────────────────────────────────────

  /**
   * /history changed (or the local status did): start a pursuit when the
   * trailing row is a turn still running and nothing here owns it yet.
   *
   *  1. FIRST pursuit for a streamId: immediately, unconditional on
   *     `streamLive` (a dead turn just replays and closes, or fails into the
   *     bounded retry budget).
   *  2. RE-ARM after a clean settle: only while the turn reads live, spaced
   *     by `nextReattachCadence`, and re-decided against a REFETCHED /history
   *     when the wait is over (`fireReattach`).
   */
  considerHistory(history: M[] | undefined, input: ConsiderHistoryInput): void {
    if (this.sessionId !== input.sessionId) {
      if (this.sessionId !== null) this.reset();
      this.sessionId = input.sessionId;
    }
    if (!input.reattachEnabled || !this.mounted) return;
    if (!history || input.isFetching) return;
    if (input.localStatus === 'streaming' || input.localStatus === 'submitted') return;
    const target = resumeTargetFromHistory(history);
    if (!target) return;
    const streamId = target.streamId;
    if (this.suppressed.has(streamId)) return;
    if (this.activeStreamId === streamId || hasResumeRecord(this.owner, streamId)) return;

    if (!this.settledOnce.has(streamId)) {
      this.arm(streamId, history);
      return;
    }

    if (this.reattachScheduledFor === streamId) return;
    if (
      !isEligibleForReattach({
        streamLive: target.streamLive,
        isFetching: input.isFetching,
        localStatus: input.localStatus,
        // Both checked just above; passed as false so the one pure function
        // stays the single statement of what makes a re-arm eligible.
        suppressed: false,
        pursuitOwned: false,
      })
    ) {
      return;
    }
    const delayMs = this.pendingReattachDelay.get(streamId) ?? DEFAULT_REATTACH_DELAY_MS;
    const epoch = this.epoch;
    this.reattachScheduledFor = streamId;
    this.reattachTimer = setTimeout(() => {
      this.reattachTimer = null;
      void this.fireReattach(streamId, epoch);
    }, delayMs);
  }

  /** The AI SDK reported a stream error (`onError`). Only an attempt in flight cares. */
  onStreamError(): void {
    this.attemptFailure?.();
  }

  /** Poison a streamId for good (a deliberate Stop seen by `onFinish`). */
  suppress(streamId: string): void {
    this.suppressed.add(streamId);
  }

  /**
   * The person pressed Stop (FRONT-2195 review F5). Poisons, synchronously,
   * every streamId that is the turn on screen — the pursuit's (in flight or
   * backing off), a pending re-arm's, and the one /history shows running —
   * and cancels both timers, so nothing can re-attach to the turn just
   * stopped. A pursuit that was backing off ends here with the snapshot put
   * back; one with an attempt in flight has that connection aborted and ends
   * through the attempt's own settle.
   */
  stop(): void {
    const toSuppress = streamIdsToSuppressOnStop({
      pursuitStreamId: this.activeStreamId,
      scheduledReattachStreamId: this.reattachScheduledFor,
      history: this.deps.latestHistory(),
    });
    for (const streamId of toSuppress) this.suppressed.add(streamId);
    this.clearRetryTimer();
    this.clearReattachTimer();
    const streamId = this.activeStreamId;
    if (!streamId) return;
    if (this.attemptFailure === null) {
      this.endPursuit(streamId, this.deps.latestHistory());
    } else {
      this.attemptAbort?.abort();
    }
  }

  // ── Pursuit steps ────────────────────────────────────────────────────────

  private arm(streamId: string, snapshot: M[]): void {
    this.pursuitStartedAt.set(streamId, Date.now());
    this.activeStreamId = streamId;
    this.resuming = true;
    this.deps.onPursuitArmed(streamId);
    this.startAttempt(streamId, snapshot);
  }

  /**
   * EVERY attempt — the first, each retry, each re-arm — starts from the
   * given /history snapshot minus its trailing partial (FRONT-2195 review
   * F3). The AI SDK continues ONTO a trailing assistant message
   * (`createStreamingUIMessageState` reuses it), and the server replays the
   * turn from the start, so a retry fired over a failed attempt's partial
   * appended the whole answer a second time.
   */
  private startAttempt(streamId: string, snapshot: M[]): void {
    this.deps.setMessages(snapshot.slice(0, -1));
    this.fireAttempt(streamId);
  }

  private fireAttempt(streamId: string): void {
    const epoch = this.epoch;
    recordResumeAttempt(this.owner, streamId, Date.now());
    this.lastResumeStatus = null;
    this.target = streamId;
    const abort = new AbortController();
    this.attemptAbort = abort;
    let failed = false;
    const onFailure = () => {
      failed = true;
      void this.afterFailedAttempt(streamId, epoch);
    };
    this.attemptFailure = onFailure;
    void this.deps.resumeStream().finally(() => {
      if (this.attemptFailure === onFailure) this.attemptFailure = null;
      if (this.attemptAbort === abort) this.attemptAbort = null;
      if (epoch !== this.epoch) return;
      if (this.target === streamId) this.target = null;
      if (!failed) this.afterCleanSettle(streamId);
    });
  }

  /**
   * A clean replay-to-end (or a tail that closed without an error) is a
   * success even when the turn is still running: clear the budget, release
   * the reseed guard, and record how long it stayed attached so the next
   * re-arm is spaced accordingly. Nothing re-arms from here directly — the
   * next /history change does, through `considerHistory`.
   */
  private afterCleanSettle(streamId: string): void {
    recordResumeSuccess(this.owner, streamId);
    this.resuming = false;
    this.activeStreamId = null;
    this.settledOnce.add(streamId);
    const startedAt = this.pursuitStartedAt.get(streamId);
    this.pursuitStartedAt.delete(streamId);
    if (startedAt != null) {
      const prior = this.cadence.get(streamId) ?? { quickSettleStreak: 0 };
      const { state, delayMs } = nextReattachCadence(prior, Date.now() - startedAt);
      this.cadence.set(streamId, state);
      this.pendingReattachDelay.set(streamId, delayMs);
    }
  }

  /**
   * An attempt failed. Decide from a REFETCHED /history (FRONT-2195 review
   * F4) — nothing else refreshes the snapshot during a chain of failed
   * reconnects, so deciding from the held one kept a finished or deleted
   * turn "live" and retried it forever. A refused response (4xx) is
   * terminal; a turn /history no longer shows running ends the pursuit with
   * that snapshot on screen.
   */
  private async afterFailedAttempt(streamId: string, epoch: number): Promise<void> {
    if (epoch !== this.epoch) return;
    if (this.suppressed.has(streamId)) {
      // Stop aborted this attempt's connection.
      this.endPursuit(streamId, this.deps.latestHistory());
      return;
    }
    const status = this.lastResumeStatus;
    let snapshot: M[] | undefined;
    let refetched = false;
    try {
      snapshot = await this.deps.refetchHistory();
      refetched = true;
    } catch {
      snapshot = this.deps.latestHistory();
    }
    if (epoch !== this.epoch || this.activeStreamId !== streamId) return;
    // FRONT-2195 review R2: a local turn started meanwhile (a queued draft
    // auto-fires the moment the server run completes) — it owns the screen.
    if (this.localTurnLive()) {
      this.abandonForLocalTurn(streamId);
      return;
    }

    const target = resumeTargetFromHistory(snapshot);
    const httpStatus = effectiveResumeStatus(status, refetched);
    if (!isTerminalResumeStatus(httpStatus) && snapshot && target?.streamId !== streamId) {
      this.finishPursuit(streamId, snapshot);
      return;
    }
    const decision = recordResumeFailure(this.owner, streamId, Date.now(), {
      turnLive: target?.streamId === streamId && target.streamLive,
      httpStatus,
    });
    if (decision.gaveUp) {
      this.giveUp(snapshot);
      return;
    }
    // FRONT-2195 review R1: put the snapshot back for the wait. Left
    // stripped, the running answer and every tool card on it (the desktop
    // card's Watch desktop / Take over included) vanished for the whole
    // backoff — and while the turn reads live the backoff never ends if the
    // resume path keeps failing. The row reads live, so it shows no
    // "interrupted" chip; the pursuit stays resuming (the reseed keeps
    // standing down) and the next attempt strips it again.
    this.show(snapshot);
    this.clearRetryTimer();
    this.retryTimer = setTimeout(() => {
      this.retryTimer = null;
      if (epoch !== this.epoch || this.activeStreamId !== streamId) return;
      // R2: never resume on top of a local turn that started during the wait.
      if (this.localTurnLive()) {
        this.abandonForLocalTurn(streamId);
        return;
      }
      const latest = this.deps.latestHistory();
      if (!latest) {
        this.endPursuit(streamId, undefined);
        return;
      }
      if (resumeTargetFromHistory(latest)?.streamId !== streamId) {
        this.finishPursuit(streamId, latest);
        return;
      }
      this.startAttempt(streamId, latest);
    }, decision.delayMs);
  }

  /**
   * The re-arm wait is over. Refetch /history and decide from THAT
   * (FRONT-2195 review F7): nothing refreshes the snapshot between a settle
   * and this timer, so the held one re-attached a turn that had finished
   * during the wait once more.
   */
  private async fireReattach(streamId: string, epoch: number): Promise<void> {
    let snapshot: M[] | undefined;
    try {
      snapshot = await this.deps.refetchHistory();
    } catch {
      snapshot = this.deps.latestHistory();
    }
    // A Stop or a reset cleared the schedule while the refetch was out.
    if (epoch !== this.epoch || this.reattachScheduledFor !== streamId) return;
    this.reattachScheduledFor = null;
    const target = resumeTargetFromHistory(snapshot);
    const eligible = isEligibleForReattach({
      streamLive: target?.streamId === streamId && target.streamLive,
      isFetching: this.deps.isHistoryFetching(),
      localStatus: this.deps.localStatus(),
      suppressed: this.suppressed.has(streamId),
      pursuitOwned: this.activeStreamId === streamId || hasResumeRecord(this.owner, streamId),
    });
    if (eligible && snapshot) this.arm(streamId, snapshot);
  }

  /** The turn is no longer running per /history: show that snapshot, pursuit over. */
  private finishPursuit(streamId: string, snapshot: M[]): void {
    recordResumeSuccess(this.owner, streamId);
    this.settledOnce.add(streamId);
    this.pursuitStartedAt.delete(streamId);
    this.resuming = false;
    this.activeStreamId = null;
    this.target = null;
    this.show(snapshot);
  }

  /** A local turn is streaming (or about to) on this instance's chat. */
  private localTurnLive(): boolean {
    const status = this.deps.localStatus();
    return status === 'streaming' || status === 'submitted';
  }

  /**
   * Put a snapshot on screen — unless a local turn is live, whose messages
   * (the person's new message among them) a snapshot would wipe (FRONT-2195
   * review R2). Every restore path goes through here.
   */
  private show(snapshot: M[] | undefined): void {
    if (!snapshot || this.localTurnLive()) return;
    this.deps.setMessages(snapshot);
  }

  /**
   * A local turn took over mid-pursuit: end the pursuit without touching the
   * screen. The local turn's own `onFinish` refreshes /history afterwards,
   * and a still-running turn is re-armed from there like any settle.
   */
  private abandonForLocalTurn(streamId: string): void {
    recordResumeSuccess(this.owner, streamId);
    this.settledOnce.add(streamId);
    this.pursuitStartedAt.delete(streamId);
    this.resuming = false;
    this.activeStreamId = null;
    this.target = null;
  }

  /**
   * Out of budget or refused: put the full snapshot back (the pursuit
   * stripped its partial, and nothing else will restore it — the SDK's failed
   * reconnect never calls onFinish) and keep the given-up record, which both
   * shows the bubble's reconnect caution and stops this instance re-arming.
   */
  private giveUp(snapshot: M[] | undefined): void {
    this.resuming = false;
    this.activeStreamId = null;
    this.target = null;
    this.show(snapshot);
  }

  /** Stop ended a pursuit that had no attempt in flight: drop it and restore. */
  private endPursuit(streamId: string, snapshot: M[] | undefined): void {
    recordResumeSuccess(this.owner, streamId);
    this.pursuitStartedAt.delete(streamId);
    this.resuming = false;
    this.activeStreamId = null;
    this.target = null;
    this.show(snapshot);
  }

  private clearRetryTimer(): void {
    if (this.retryTimer != null) {
      clearTimeout(this.retryTimer);
      this.retryTimer = null;
    }
  }

  private clearReattachTimer(): void {
    if (this.reattachTimer != null) {
      clearTimeout(this.reattachTimer);
      this.reattachTimer = null;
    }
    this.reattachScheduledFor = null;
  }
}
